import { z } from 'zod';

import {
  ApplePayConfiguration,
  BrandAgreement,
  GooglePayConfiguration,
  Maybe,
  PayAtPump,
  PayInside,
  KountConfiguration,
  PinCodeConfiguration,
  WalletConfiguration as GraphQLWalletConfiguration,
  DeleteAccountConfiguration,
  PointsStrategy,
  PointExpirationStrategy,
  GetAppConfigQuery,
  CommunicationPreferenceConfiguration,
  EaivAgeVerificationConfiguration,
  EaivIdentityVerificationConfiguration,
  ProductInterestsConfiguration,
  AppRatingConfiguration,
  AuthMethod,
  SmsOptInConfiguration,
  OtpChannel,
  MobilePaymentsReceiptsViewConfiguration,
  StoreLocatorConfiguration as StoreLocatorRemoteConfiguration,
  ReferralProgramConfigFragment,
  OrderAheadConfiguration,
  OrderAheadConfigurationProviderVroomFragment,
  OrderAheadConfigurationProviderOloFragment,
  OrderAheadConfigurationProviderLulaFragment,
  ExternalIdentityVerificationProvider,
  UserExperienceConfig,
  DeleteAccountMutation,
  PushNotificationReportingConfig,
} from '@ocx/graphql';

import { SupportConfiguration } from './sub-configurations/support/support-configuration.types';
import { FuelGradesConfigurationSchema, LocalRetailerConfigurationSchema } from './app-config.schema';

export type RemoteConfiguration = GetAppConfigQuery['appConfig'];

export type LoyaltyProgramConfiguration = {
  // TODO: deprecate LoyaltyProgramConfiguration.pointsStrategy
  /**
   * @deprecated
   * Please use loyaltyCurrency.pointsStrategy instead
   */
  pointsStrategy: PointsStrategy;
  pointExpiration: PointExpirationConfiguration;
  homePageOffersLimit: number;
  redeemOfferWithCompletedProfileOnly: boolean;
  membershipMinAge: number;
  membershipMaxAge: number;
  isExplicitTermsAcceptanceRequired: boolean;
  isExplicitSmsPromotionAcceptanceEnabled: boolean;
  isPromocodesEnabled: boolean;
  referralProgramConfig: ReferralProgramConfigFragment | null;
};

type StoreLocatorRuntimeConfiguration = {
  googleMapsApiKey: string;
  defaultLocation: {
    lat: number;
    lng: number;
  };
  watchTimeout: number;
  atTheStoreDistance: number;
  fuelPricesMode: StoreLocatorRemoteConfiguration['fuelPricesMode'];
};

export type OrderAheadRuntimeConfiguration =
  | {
      enabled: true;
      navbar: OrderAheadConfiguration['navbar'];
      provider: OrderAheadProviderConfiguration;
    }
  | { enabled: false };

export type RuntimeConfig = {
  release: string;
  build: string;
  version: string;
  retailer: SchemaConfig['retailer'];
  brandUuid: string;
  chainId: string;
  subBrandName: SchemaConfig['subBrandName'];
  subBrandCode: SchemaConfig['subBrandCode'];

  userExperience: UserExperienceConfig;
  termsConditions: BrandAgreement;
  privacyPolicy: BrandAgreement;
  support: SupportConfiguration;
  loyaltyProgram: LoyaltyProgramConfiguration;
  loyaltyCurrency: LoyaltyCurrencyConfig;
  payAtPump: PayAtPump;
  payInside: PayInside;
  transaction: {
    pollInterval: number;
    errorLimit: number;
  };
  applePay: Maybe<ApplePayConfiguration>;
  googlePay: Maybe<GooglePayConfiguration>;
  marketing: {
    title: SchemaConfig['marketing']['title'];
  };
  rudderStack: SchemaConfig['rudderStack'] & {
    enabled: boolean;
  };
  totalAvailableRollback: {
    enabled: boolean;
  };
  totalPointsEarned: {
    enabled: boolean;
  };
  surveys: {
    enabled: boolean;
  };
  localRetailerConfig: LocalRetailerConfiguration;

  kount: Maybe<KountConfiguration>;
  pinCode: Partial<Pick<PinCodeConfiguration, 'blockingDuration' | 'attemptThrottle' | 'maxAttempts'>> & {
    pinInMemoryTtl: number;
  };
  wallet: WalletConfiguration;
  deleteAccount: DeleteAccountConfiguration;
  paymentProvider: PaymentProviderConfiguration;
  communicationPreference: CommunicationPreferenceConfiguration;
  eaivAgeVerification: EaivAgeVerificationConfiguration;
  eaivIdentityVerification: EaivIdentityVerificationConfiguration;
  productInterests: ProductInterestsConfiguration;
  appRating: AppRatingConfiguration;
  auth: {
    primaryMethod: AuthMethod;
    otp: {
      enabled: boolean;
      channels: OtpChannel[];
    };
  };
  smsOptIn: SmsOptInConfiguration;
  mobilePaymentsReceiptsView: MobilePaymentsReceiptsViewConfiguration;
  storeLocator: StoreLocatorRuntimeConfiguration;
  smsOptedOutWarning: SmsOptedOutWarningConfiguration;
  orderAhead: OrderAheadRuntimeConfiguration;
  externalIdentityVerification: { enabled: false } | { enabled: true; provider: ExternalIdentityVerificationProvider };
  personalDataShareOptOut: { enabled: false } | { enabled: true; optOutUrl: string };
  appsFlyer: AppsFlyerConfiguration;
  localOffers:
    | {
        enabled: true;
        radiusInMeters: number;
      }
    | { enabled: false };
  spireEnrollment: SpireEnrollmentConfiguration;
  cookieConsent:
    | { enabled: false }
    | { provider: 'cookiebot'; sdkUrl: string; enabled: true; consentEditingEnabled: boolean };
  loyaltyIdBarcode: {
    enabled: boolean;
  };
  pushNotificationReporting: PushNotificationReportingConfig;
  assets: Record<AssetKey, string>;
};

export type FuelGradesConfiguration = z.infer<typeof FuelGradesConfigurationSchema>;
export type LocalRetailerConfiguration = z.infer<typeof LocalRetailerConfigurationSchema>;

export enum PAYMENT_PROVIDER_TYPE {
  FISERV = 'FISERV',
  WORLDPAY = 'WORLDPAY',
  PAR_PAY = 'PAR_PAY',
}
export type PaymentProviderBaseConfiguration = {
  type: PAYMENT_PROVIDER_TYPE;
};
export type PaymentProviderFiservConfiguration = PaymentProviderBaseConfiguration & {
  type: PAYMENT_PROVIDER_TYPE.FISERV;
};
export type PaymentProviderParPayConfiguration = PaymentProviderBaseConfiguration & {
  type: PAYMENT_PROVIDER_TYPE.PAR_PAY;
};
export type PaymentProviderWorldpayConfiguration = PaymentProviderBaseConfiguration & {
  type: PAYMENT_PROVIDER_TYPE.WORLDPAY;
  eProtectPayPageId: string;
  eProtectGooglePayPageId: string;
  eProtectWebSdkApiUrl: string;
  eProtectWebSdkUrl: string;
};
export type PaymentProviderConfiguration =
  | PaymentProviderFiservConfiguration
  | PaymentProviderParPayConfiguration
  | PaymentProviderWorldpayConfiguration
  | null;

export const ORDER_AHEAD_PROVIDER_TYPE_OLO = 'olo';
export const ORDER_AHEAD_PROVIDER_TYPE_LULA = 'lula';
export const ORDER_AHEAD_PROVIDER_TYPE_ALTAINE = 'altaine';
export const ORDER_AHEAD_PROVIDER_TYPE_VROOM = 'vroom';

export type OrderAheadProviderVroomConfiguration = {
  type: typeof ORDER_AHEAD_PROVIDER_TYPE_VROOM;
} & OrderAheadConfigurationProviderVroomFragment;
export type OrderAheadProviderOloConfiguration = {
  type: typeof ORDER_AHEAD_PROVIDER_TYPE_OLO;
} & OrderAheadConfigurationProviderOloFragment;
export type OrderAheadProviderLulaConfiguration = {
  type: typeof ORDER_AHEAD_PROVIDER_TYPE_LULA;
} & OrderAheadConfigurationProviderLulaFragment;

// Extend with other providers when needed
export type OrderAheadProviderConfiguration =
  | OrderAheadProviderVroomConfiguration
  | OrderAheadProviderOloConfiguration
  | OrderAheadProviderLulaConfiguration;

export type WalletConfiguration = GraphQLWalletConfiguration & {
  sizeLimit: number;
  prepaidAccountTypeEnabled: boolean;
};

export type PointExpirationConfiguration = {
  strategy: PointExpirationStrategy;
  notificationDays: number | null;
  isFifo: boolean;
  isActivityBased: boolean;
  isNone: boolean;
};

export type SmsOptedOutWarningConfiguration = {
  enabled: boolean;
  smsOptInPhoneNumber: string;
  smsOptInKeyword: string;
};

export type AppsFlyerConfiguration =
  | {
      enabled: true;
      appId: string;
      devKey: string;
      webDevKey: string | null;
    }
  | { enabled: false };

export type SpireEnrollmentConfiguration =
  | {
      enabled: false;
    }
  | {
      enabled: true;
      iframeUrl: string;
      partnerId: string | null;
    };

/**
 * All supported asset keys used across configuration.
 */
export type AssetKey =
  | 'Achievement'
  | 'FuelingCompleted'
  | 'FuelingProcess'
  | 'NavLogo'
  | 'MapMarker'
  | 'HowDoesItWorkIcon'
  | 'UserNotVerifiedIcon'
  | 'UserVerifiedIcon'
  | 'CoinIcon'
  | 'CoinsIcon'
  | 'DollarCoinIcon'
  | 'DollarsCoinsIcon'
  | 'Medal'
  | 'AgeRestricted'
  | 'NoData'
  | 'ReadyToUseRewards'
  | 'TotalAvailableRollback'
  | 'PrepaidIcon'
  | 'ForceUpgradeBackground'
  | 'VisaIcon'
  | 'MastercardIcon'
  | 'DiscoverIcon'
  | 'AmexIcon'
  | 'ApplePayIcon'
  | 'GooglePayIcon'
  | 'GeneralCardIcon'
  | 'SplitPaymentIcon'
  | 'UnknownPaymentInstrumentIcon'
  | 'CashIcon';

// Defined locally to avoid circular project-level deps with @ocx/data-membership and
// @ocx/feat-loyalty-currency (both import @ocx-app/* creating cycles via the frontend project).
// These are structurally identical to the types in those libs.

export type MembershipConfigOverrides = {
  walletEnabled?: boolean;
  prepaidCardsEnabled?: boolean;
  payInsideEnabled?: boolean;
  payAtPumpEnabled?: boolean;
  deleteAccountEnabled?: boolean;
  deleteAccountMutation?: DeleteAccountMutation;
  appRatingEnabled?: boolean;
  promptIntervalDays?: number;
  transactionHistoryLookupDays?: number;
  productInterestsEnabled?: boolean;
  productInterestsEAIVPromotionOnProfileCompletedEnabled?: boolean;
  eaivAgeVerificationEnabled?: boolean;
  eaivIdentityVerificationEnabled?: boolean;
  totalAvailableRollbackEnabled?: boolean;
  loyaltyProgramPromocodesEnabled?: boolean;
  mobilePaymentsReceiptsViewEnabled?: boolean;
};

export type LoyaltyCurrencyIconConfig = Record<'single' | 'multiple', AssetKey>;

export type LoyaltyCurrencyConfig =
  | { pointsStrategy: PointsStrategy.NotUsed; iconConfig: null }
  | { pointsStrategy: PointsStrategy.Points | PointsStrategy.Cents; iconConfig: LoyaltyCurrencyIconConfig };

// TODO Duplication of apps/frontend/src/global.d.ts
// Refactor and remove duplications
// For now, used to make useConfiguration(<path>) work correctly in libs
type SchemaConfig = {
  environment: string;
  retailer: string;
  release: string;
  build: string;
  gitRevision: string;
  subBrandName: string;
  subBrandCode: string;
  api: {
    url: string;
    key: string;
  };
  rudderStack: {
    writeKey: string;
    dataPlaneUrl: string;
  };
  sentry: {
    dsn?: string;
    tracesSampleRate?: number;
  };
  marketing: {
    title: string;
  };
};
